import React, { useEffect, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { toast as sonnerToast } from 'sonner';
import { CheckCircleIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { useAuth } from '@/contexts/AuthContext';
import { logFirebaseEvent } from '@/lib/tracking';

const PaymentSuccessPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { fetchUserSettings, userSettings } = useAuth(); // Removed currentUser as it's not directly used here
  const sessionId = searchParams.get('checkout_session_id');
  const [processedSessionId, setProcessedSessionId] = useState<string | null>(null);

  useEffect(() => {
    const handleSuccessfulPayment = async () => {
      if (!sessionId || sessionId === processedSessionId) {
        // If no session ID or this session ID has already been processed, do nothing further.
        // This also handles the case where user navigates away and back.
        if (!sessionId) { // If somehow landed here without session ID after initial load
            navigate('/studio', { replace: true });
        }
        return;
      }

      setProcessedSessionId(sessionId); // Mark this session ID as processed

      const latestSettings = await fetchUserSettings();
      const plan = latestSettings?.planDetails ?? userSettings?.planDetails;
      const purchaseValue = plan?.monthlyPrice || 0; 
      const currency = 'USD';

      sonnerToast.success('Payment successful! Your subscription is active.', {
        id: `payment-success-${sessionId}`, // Use a unique ID for the toast to prevent duplicates if sonner itself re-renders
        description: "You'll be redirected to your dashboard shortly.",
        duration: 5000,
      });

      // --- Tracking Events (only fire once per session ID) ---
      logFirebaseEvent('purchase_client_confirmation', {
        transaction_id: sessionId,
        value: purchaseValue,
        currency: currency,
        items: plan ? [{ item_id: plan.priceId, item_name: plan.name, quantity: 1 }] : [],
      });
      // --- End Tracking Events ---
      
      const timer = setTimeout(() => {
        navigate('/studio', { replace: true });
      }, 4000);
      return () => clearTimeout(timer);
    };

    if (sessionId) {
      handleSuccessfulPayment();
    } else {
      // If no session ID at all on initial load, redirect away.
      // This handles cases where user directly navigates to /payment-success without a session.
      sonnerToast.info('No payment session found, redirecting to dashboard.');
       const timer = setTimeout(() => {
        navigate('/studio', { replace: true });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [sessionId, navigate, fetchUserSettings, processedSessionId, userSettings]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md text-center">
        <CardHeader>
          <CheckCircleIcon className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <CardTitle className="text-2xl font-semibold">Payment Successful!</CardTitle>
          <CardDescription className="text-muted-foreground">
            Thank you! Your subscription {userSettings?.planDetails?.name ? `to the ${userSettings.planDetails.name} plan` : ''} is now active.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p>You will be redirected to your dashboard automatically.</p>
          <Button asChild className="w-full">
            <Link to="/studio">Go to Dashboard Now</Link>
          </Button>
        </CardContent>
        <CardFooter>
            {sessionId && <p className="text-xs text-muted-foreground mt-2 mx-auto">Ref: {sessionId.substring(0,20)}...</p>}
        </CardFooter>
      </Card>
    </div>
  );
};

export default PaymentSuccessPage;
